import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GlobalOutlined } from '@kufox/icons';
import { ClassNames } from '@kufox/mui/emotion';
import clsx from 'clsx';
import Tabs from '@kufox/mui/NewTabs';
import useTheme from '@kufox/mui/hooks/useTheme';
import { isArray, map } from 'lodash';
import { CurrencySymbol } from '../config';
import { namespace } from '../model';
import { useLang } from '../../hookTool';
import checkIcon from '../../../static/newHeader/check.svg';
import NoSSGModal from '../../components/NoSSGModal';
import { NoSSG } from '../../common/tools';

const { Tab } = Tabs;
const types = ['lang', 'currency'];
const useStyles = ({ color }) => {
  return {
    tabsWrapper: `
      margin: 16px 32px 0;
      background: ${color.base};
      color: ${color.text};
    `,
    dialogWrapper: `
      animation: unset !important;
      & > div {
        background: ${color.base};
        border-radius: 4px;
        box-shadow: 0px 10px 60px rgba(0, 0, 0, 0.1);
        overflow: hidden;
      }
      & > div > div {
        padding: 0;
        & > div {
          margin-bottom: 0;
          & > div {
            top: 5px;
            right: 30px;
            z-index: 1;
          }
        }
      }
    `,
    overlayWrapper: `
      min-width: 400px;
      height: 530px;
      margin: 10px 0 20px;
      padding: 8px 0;
      display: flex;
      background: ${color.base};
      flex-direction: column;
      & .currencyGroup {
        min-width: 1000px;
      }
      & .langGroup {
        min-width: 600px;
      }
      & .group {
        flex: 1;
        height: 480px;
        display: flex;
        flex-wrap: wrap;
        flex-direction: row;
        writing-mode: vertical-lr;
        margin-bottom: 8px;
        &::-webkit-scrollbar {
          background: transparent;
          width: 4px;
        }
        &::-webkit-scrollbar-track {
          background: transparent;
        }
        &::-webkit-scrollbar-thumb {
          border-radius: 2px;
          background: rgba(255, 255, 255, 0.24);
        }
        & .title {
          color: ${color.text60};
          padding: 8px 48px;
          font-size: 12px;
          line-height: 20px;
        }
        & .menuItem {
          padding: 13px 32px;
          color: ${color.text};
          display: flex;
          align-items: center;
          font-size: 14px;
          line-height: 1.2;
          cursor: pointer;
          width: 200px;
          height: 48px;
          writing-mode: horizontal-tb;
          & span {
            position: relative;
          }
          &:hover {
            background: ${color.cover4};
          }
          & .iconCurrency {
            margin-right: 8px;
            margin-left: 4px;
          }
        }
        & .activeItem {
          color: ${color.primary};
          & img {
            position: absolute;
            top: 50%;
            right: -18px;
            transform: translate3d(0, -50%, 0);
          }
        }
      }
    `,
    overlayTitle: `
      font-size: 16px;
      line-height: 26px;
      color: ${color.text};
      font-weight: bold;
      margin: 0 32px;
    `,
    inDrawer: `
      font-size: 14px;
      line-height: 22px;
      color: ${color.text};
      .group {
        margin-top: 12px;
        .title {
          color: ${color.text};
          margin: 20px 32px;
          font-size: 20px;
          font-family: URWDIN-Medium;
        }
        .menuItem {
          padding: 6px 32px;
          display: flex;
          justify-content: space-between;
          cursor: pointer;
          color: ${color.text};
          &.activeItem {
            flex-direction: row-reverse;
            & span {
              width: 100%;
              display: flex;
              justify-content: space-between;
            }
          }
          &:hover {
            background: ${color.cover4};
          }
        }
      }
    `,
    tabsSpan: `
      color: ${color.text};
      height: 35px;
      font-weight: 500;
    `,
    activeTabsSpan: `
      color: ${color.primary};
    `,
  };
};

const I18nBox = (props) => {
  const {
    currentLang,
    currency,
    onCurrencyChange,
    onLangChange,
    inDrawer,
    closeI18nDrawer,
    type = types[0],
    showType = '', // 显示的类型
    className = '',
    surportLanguages, // 支持的语言
    outerCurrencies = undefined, // 使用外部的currenc 数据，减少请求
  } = props;
  const theme = useTheme();
  const classes = useStyles({ color: theme.colors });
  const { t } = useLang();

  const { langList, currencyList } = useSelector((state) => state[namespace]);
  const dispatch = useDispatch();

  const [modalVisible, setModalVisible] = useState(false);
  const [tab, setTab] = useState(types[0]);

  const showModal = useCallback(() => {
    setModalVisible(true);
    setTab(showType === types[0] ? types[0] : types[1]);
  }, [showType]);

  const closeModal = useCallback(() => {
    setModalVisible(false);
  }, []);

  const tabChange = useCallback((val) => {
    setTab(val);
  }, []);

  useEffect(() => {
    // 如果未使用外部数据则使用原本请求
    console.log(`${namespace}/pullRates`, outerCurrencies);
    dispatch({
      type: `${namespace}/pullRates`,
      payload: {
        currencyListData: outerCurrencies,
      },
    });
  }, [outerCurrencies]);

  useEffect(() => {
    dispatch({ type: `${namespace}/pullPrices`, payload: { currency } });
  }, [currency]);

  const handleCurrencyChange = (currency) => {
    if (closeI18nDrawer) {
      closeI18nDrawer();
    }
    onCurrencyChange && onCurrencyChange(currency);
    closeModal();
  };

  const handleLangChange = (item) => {
    if (inDrawer && closeI18nDrawer) {
      closeI18nDrawer();
    }
    onLangChange && onLangChange(item[0]);
  };
  // 根据传入的 surportLanguages 过滤出支持的语种，不传或为空时使用全语种
  const filterSurportLanguages = () => {
    let data = langList;
    if (surportLanguages && isArray(surportLanguages) && surportLanguages.length > 0) {
      data = langList.filter((item) => {
        const [langCode] = item;
        if (surportLanguages.find((ele) => ele === langCode)) {
          return item;
        }
        return null;
      });
    }
    return data;
  };

  const getLang = () => {
    let _currentLang = currentLang;
    const _languages = filterSurportLanguages();
    const data = _languages.filter((item) => {
      const [langCode, langName] = item;
      if (langCode === currentLang) {
        _currentLang = langName;
      }
      return langCode !== currentLang;
    });
    if (!langList || !langList.length) {
      return null;
    }

    return (
      <div className={clsx('group', { 'langGroup': data.length >= 20 })}>
        {inDrawer && <div className="title">{t('language')}</div>}
        <div className="menuItem activeItem">
          <span>
            {_currentLang}
            <img className="icon" src={checkIcon} alt="" />
          </span>
        </div>
        {map(data, (item) => {
          return (
            <div key={item[0]} onClick={() => handleLangChange(item)} className="menuItem">
              <span>{item[1]}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const getCurrency = () => {
    const obj = {};
    CurrencySymbol.forEach((item) => {
      const array = item.pair.split('_');
      obj[array[0]] = item.symbol;
    });

    const data = currencyList.filter((item) => {
      return item !== currency;
    });
    if (!currencyList || !currencyList.length) {
      return null;
    }
    const symbol = JSON.parse(JSON.stringify(obj, null, 2));
    return (
      <div className="group currencyGroup">
        {inDrawer && <div className="title">{t('currency')}</div>}
        <div className="menuItem activeItem">
          <span>
            {currency}-{symbol[currency] || currency}
            <img className="icon" src={checkIcon} alt="" />
          </span>
        </div>
        {map(data, (item) => {
          return (
            <div key={item} onClick={() => handleCurrencyChange(item)} className="menuItem">
              <span>
                {item}-{symbol[item] || item}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const Title = () => {
    return (
      <ClassNames>
        {({ css }) => (
          <div className={css(classes.tabsWrapper)}>
            <Tabs value={tab} onChange={(e, v) => tabChange(v)}>
              <Tab
                value={types[0]}
                label={
                  <span
                    className={clsx(
                      css(classes.tabsSpan),
                      tab === types[0] ? css(classes.activeTabsSpan) : '',
                    )}
                  >
                    {t('language')}
                  </span>
                }
              />
              {!currencyList || !currencyList.length ? null : (
                <Tab
                  label={
                    <span
                      className={clsx(
                        css(classes.tabsSpan),
                        tab === types[1] ? css(classes.activeTabsSpan) : '',
                      )}
                    >
                      {t('currency')}
                    </span>
                  }
                  value={types[1]}
                />
              )}
            </Tabs>
          </div>
        )}
      </ClassNames>
    );
  };

  if (inDrawer) {
    return (
      <ClassNames>
        {({ css }) => (
          <div className={css(classes.inDrawer)}>
            {type === types[0] ? getLang() : getCurrency()}
          </div>
        )}
      </ClassNames>
    );
  }

  return (
    <ClassNames>
      {({ css }) => (
        <>
          <div className={className || ''} onClick={showModal}>
            {showType === types[0] ? (
              <GlobalOutlined size="36" className="navIcon" />
            ) : (
              <NoSSG>
                <span>{currency}</span>
              </NoSSG>
            )}
          </div>
          <NoSSGModal
            maskClosable
            showCloseX
            footer={<span />}
            title=""
            size="large"
            open={modalVisible}
            onCancel={closeModal}
            wrapClassName={css(classes.dialogWrapper)}
          >
            <Title />
            {tab === types[0] ? (
              <div className={css(classes.overlayWrapper)}>
                <div className={css(classes.overlayTitle)}>{t('newheader.select.lang')}</div>
                {getLang()}
              </div>
            ) : (
              <div className={css(classes.overlayWrapper)}>
                <div className={css(classes.overlayTitle)}>{t('newheader.select.currency')}</div>
                {getCurrency()}
              </div>
            )}
          </NoSSGModal>
        </>
      )}
    </ClassNames>
  );
};

export default I18nBox;
